// @ts-nocheck
/*
 * Port of views/dashboard/ivr/IVR/nodes/ApiNode.js (830 lines).
 * BYTE-IDENTICAL — reactflow → @xyflow/react.
 *
 * Preserves verbatim: `newAction` shape including `webhook_id`,
 * `webhook_name`, `api_webhook_details` (url/request_type/header/params/
 * response_parameters), `selected_fixed_json_keys`, `selected_response_params`,
 * `pass_to_webhook`, `play_in_next_node`, `selected_available_params`
 * (conditional on play_in_next_node), `selected_custom_params[]` shape
 * `{name, nodeId, enable: true}`, `param_value_mapping`,
 * `header_value_mapping`, `fixed_json_value_mapping`, single-digit
 * DTMF dtmf_digit auto-injection vs multi-digit use_dtmf_input opt-in.
 * Single atomic setNodes update propagates `passToNextNode` to
 * connected playNode/gatherNode targets, matching v1 verbatim.
 *
 * GET vs POST payload merge order: for POST, parseInto(payload) THEN
 * parseInto(fixed_json) with seen-key dedup; for GET, only fixed_json —
 * this ordering matters for wire params.
 */
import React, { useContext, useState, useMemo, useRef, useEffect } from 'react';
import { Button, Form } from './_rbShim';
import { Handle, useReactFlow } from '@xyflow/react';
import { NodesContext } from '../provider/IvrContext';
import { toast } from 'sonner';
import {
  Webhook as WebhookIcon,
  Settings,
  Trash2,
  CheckCircle2,
  ArrowRight,
  ClipboardList,
  Code,
  Info,
  Key,
  Keyboard,
  Pencil,
} from 'lucide-react';
import IvrDrawer from '../Helpers/IvrDrawer';
import {
  triggerHandleConnect,
  isCounterFlowVariable,
  flowVariableToken,
} from '../Helpers/utils';
import { ROUTES } from '@/utilities/constant';

const ApiNode = ({ data, id }: any) => {
  const { setNodes, getNodes, getEdges } = useReactFlow();
  const {
    updateNodeData,
    onRemove,
    apiWebhookList = [],
    flowVariablesList = [],
  } = useContext(NodesContext) as any;

  // Flow variables (master table) offered as replacements; Counter is only
  // meaningful inside a Conditional node.
  const replacementFlowVariables = flowVariablesList.filter(
    (v: any) => v?.value && !isCounterFlowVariable(v.name, v.value),
  );
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [errors, setErrors] = useState<any>({});

  const action = data.action || {};
  const [draftWebhookId, setDraftWebhookId] = useState(
    action.webhook_id ? String(action.webhook_id) : '',
  );
  const [selectedFixedKeys, setSelectedFixedKeys] = useState<any>(
    () => new Set(Object.keys(action.selected_fixed_json_keys || {})),
  );
  const [selectedResponseParams, setSelectedResponseParams] = useState<any>(
    () => new Set(Object.keys(action.selected_response_params || {})),
  );
  const [passToWebhook, setPassToWebhook] = useState(
    action.pass_to_webhook || false,
  );
  const [playInNextNode, setPlayInNextNode] = useState(
    action.play_in_next_node || false,
  );
  const [selectedAvailableParams, setSelectedAvailableParams] = useState<any[]>(
    Array.isArray(action.selected_available_params)
      ? action.selected_available_params
      : [],
  );
  const [useDtmfInput, setUseDtmfInput] = useState(
    action.use_dtmf_input || false,
  );
  const [dtmfParentPassEnabled, setDtmfParentPassEnabled] = useState(false);
  const [ancestorVars, setAncestorVars] = useState<any[]>([]);
  const [selectedCustomParams, setSelectedCustomParams] = useState<any[]>(
    Array.isArray(action.selected_custom_params)
      ? action.selected_custom_params.map((v: any) =>
          typeof v === 'string' ? v : v.name,
        )
      : [],
  );
  const [customParamsOpen, setCustomParamsOpen] = useState(false);
  const customParamsRef = useRef<any>(null);
  const [paramValueMapping, setParamValueMapping] = useState<any>(
    action.param_value_mapping || {},
  );
  const [headerValueMapping, setHeaderValueMapping] = useState<any>(
    action.header_value_mapping || {},
  );
  const [payloadEditOpen, setPayloadEditOpen] = useState(false);
  const [headersEditOpen, setHeadersEditOpen] = useState(false);
  const [fixedEditOpen, setFixedEditOpen] = useState(false);
  const [fixedJsonValueMapping, setFixedJsonValueMapping] = useState<any>(
    action.fixed_json_value_mapping || {},
  );

  useEffect(() => {
    const handleClickOutside = (e: any) => {
      if (
        customParamsRef.current &&
        !customParamsRef.current.contains(e.target)
      ) {
        setCustomParamsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateNode = (updates: any) => updateNodeData(id, updates);

  const getAncestorVariables = () => {
    const vars: any[] = [];
    const seenNames = new Set();
    let currentId = id;

    while (true) {
      const incomingEdge = getEdges().find((e: any) => e.target === currentId);
      if (!incomingEdge) break;
      const parentNode = getNodes().find(
        (n: any) => n.id === incomingEdge.source,
      );
      if (!parentNode) break;

      if (
        Array.isArray(parentNode?.data?.availableVariables) &&
        parentNode.data.availableVariables.length > 0
      ) {
        parentNode.data.availableVariables.forEach((v: any) => {
          if (!seenNames.has(v.name)) {
            seenNames.add(v.name);
            vars.push(v);
          }
        });
      }

      if (
        (parentNode?.type === 'apiNode' || parentNode?.data?.verb === 'api') &&
        Array.isArray(parentNode?.data?.action?.selected_available_params)
      ) {
        parentNode.data.action.selected_available_params.forEach(
          (param: any) => {
            const varName = `{${param}}`;
            if (!seenNames.has(varName)) {
              seenNames.add(varName);
              vars.push({ name: varName, nodeId: parentNode.id, enable: false });
            }
          },
        );
      }

      currentId = parentNode.id;
    }

    return vars;
  };

  const selectedWebhook = useMemo(
    () =>
      apiWebhookList.find((w: any) => String(w.id) === draftWebhookId) || null,
    [apiWebhookList, draftWebhookId],
  );

  const fixedJsonEntries = useMemo(() => {
    if (!selectedWebhook?.fixed_json) return [];
    try {
      const parsed = JSON.parse(selectedWebhook.fixed_json);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return Object.entries(parsed).map(([key, value]: any) => ({
          key,
          value:
            typeof value === 'object' ? JSON.stringify(value) : String(value),
        }));
      }
    } catch {}
    return [];
  }, [selectedWebhook]);

  const responseParams = useMemo(() => {
    if (!selectedWebhook?.response_parameters) return [];
    try {
      const parsed = JSON.parse(selectedWebhook.response_parameters);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return Object.keys(parsed).filter((k: any) => !/^\d+$/.test(k));
      }
      return [];
    } catch {
      return [];
    }
  }, [selectedWebhook]);

  const webhookHeaderEntries = useMemo(() => {
    if (!selectedWebhook?.header) return [];
    try {
      const parsed = JSON.parse(selectedWebhook.header);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return Object.entries(parsed);
      }
    } catch {}
    return [];
  }, [selectedWebhook]);

  const webhookPayloadInfo: any = useMemo(() => {
    if (!selectedWebhook) return null;
    const isPost = selectedWebhook.request_type === 'POST';
    const entries: any[] = [];
    const seenKeys = new Set();

    const parseInto = (raw: any) => {
      if (!raw) return;
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          Object.entries(parsed).forEach(([k, v]: any) => {
            if (!seenKeys.has(k)) {
              seenKeys.add(k);
              entries.push([k, v]);
            }
          });
        }
      } catch {}
    };

    // For POST: payload is the request body; fixed_json holds extra request params
    // For GET: fixed_json holds the query/request params
    if (isPost) {
      parseInto(selectedWebhook.payload);
      parseInto(selectedWebhook.fixed_json);
    } else {
      parseInto(selectedWebhook.fixed_json);
    }

    if (entries.length === 0) return null;
    return { isPost, entries };
  }, [selectedWebhook]);

  const openModal = () => {
    const act = data.action || {};
    setDraftWebhookId(act.webhook_id ? String(act.webhook_id) : '');
    setSelectedFixedKeys(new Set(Object.keys(act.selected_fixed_json_keys || {})));
    setSelectedResponseParams(
      new Set(Object.keys(act.selected_response_params || {})),
    );
    setPassToWebhook(act.pass_to_webhook || false);
    setPlayInNextNode(act.play_in_next_node || false);
    setSelectedAvailableParams(
      Array.isArray(act.selected_available_params)
        ? act.selected_available_params
        : [],
    );
    setUseDtmfInput(act.use_dtmf_input || false);
    setSelectedCustomParams(
      Array.isArray(act.selected_custom_params)
        ? act.selected_custom_params.map((v: any) =>
            typeof v === 'string' ? v : v.name,
          )
        : [],
    );
    setAncestorVars(getAncestorVariables());
    setParamValueMapping(act.param_value_mapping || {});
    setHeaderValueMapping(act.header_value_mapping || {});
    setPayloadEditOpen(false);
    setHeadersEditOpen(false);
    setFixedEditOpen(false);
    setFixedJsonValueMapping(act.fixed_json_value_mapping || {});

    // Dynamically read parent DTMF node's passToNextNode from live graph state
    const incomingEdge = getEdges().find((e: any) => e.target === id);
    if (incomingEdge) {
      const parentNode = getNodes().find(
        (n: any) => n.id === incomingEdge.source,
      );
      const isMultiDtmfParent =
        parentNode?.type === 'gatherNode' &&
        Number(parentNode?.data?.numDigits) > 1;
      setDtmfParentPassEnabled(
        isMultiDtmfParent ? parentNode.data.passToNextNode || false : false,
      );
    } else {
      setDtmfParentPassEnabled(false);
    }

    setErrors({});
    setModalIsOpen(true);
  };

  const handleClose = () => {
    setErrors({});
    setModalIsOpen(false);
  };

  const handleWebhookChange = (e: any) => {
    setDraftWebhookId(e.target.value);
    setSelectedFixedKeys(new Set());
    setSelectedResponseParams(new Set());
    setSelectedCustomParams([]);
    setParamValueMapping({});
    setHeaderValueMapping({});
    setFixedJsonValueMapping({});
    setErrors({});
  };

  const toggleFixedKey = (key: any) => {
    setSelectedFixedKeys((prev: any) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleResponseParam = (key: any) => {
    setSelectedResponseParams((prev: any) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleSave = () => {
    if (!draftWebhookId) {
      setErrors({ webhook: 'Please select an API webhook' });
      return;
    }

    const fixedJsonObj: any = {};
    fixedJsonEntries
      .filter(({ key }: any) => selectedFixedKeys.has(key))
      .forEach(({ key, value }: any) => {
        fixedJsonObj[key] = value;
      });

    const responseParamsObj: any = {};
    [...selectedResponseParams]
      .filter((key: any) => responseParams.includes(key))
      .forEach((key: any) => {
        responseParamsObj[key] = `{${key}}`;
      });

    // Build final params with replacements applied
    const finalParams: any = {};
    if (webhookPayloadInfo && webhookPayloadInfo.entries.length > 0) {
      webhookPayloadInfo.entries.forEach(([key, defaultVal]: any) => {
        finalParams[key] =
          paramValueMapping[key] !== undefined
            ? paramValueMapping[key]
            : typeof defaultVal === 'object'
            ? JSON.stringify(defaultVal)
            : String(defaultVal);
      });
    }

    const parseJsonSafe = (raw: any) => {
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return raw;
      }
    };

    // Build final headers with replacements applied
    const finalHeaders: any = {};
    if (selectedWebhook?.header) {
      try {
        const parsedHeader = JSON.parse(selectedWebhook.header);
        if (parsedHeader && typeof parsedHeader === 'object') {
          Object.entries(parsedHeader).forEach(([key, val]: any) => {
            finalHeaders[key] =
              headerValueMapping[key] !== undefined
                ? headerValueMapping[key]
                : String(val);
          });
        }
      } catch {}
    }

    const apiWebhookDetails = selectedWebhook
      ? {
          url: selectedWebhook.url || null,
          request_type: selectedWebhook.request_type || null,
          header:
            Object.keys(finalHeaders).length > 0
              ? finalHeaders
              : parseJsonSafe(selectedWebhook.header),
          params: Object.keys(finalParams).length > 0 ? finalParams : null,
          response_parameters: parseJsonSafe(selectedWebhook.response_parameters),
        }
      : null;

    const newAction: any = {
      webhook_id: selectedWebhook?.id,
      webhook_name:
        selectedWebhook?.webhook_name || selectedWebhook?.name || '',
      api_webhook_details: apiWebhookDetails,
      selected_fixed_json_keys: fixedJsonObj,
      selected_response_params: responseParamsObj,
      pass_to_webhook: passToWebhook,
      play_in_next_node: playInNextNode,
      selected_available_params: playInNextNode
        ? selectedAvailableParams
        : [],
      selected_custom_params: ancestorVars
        .filter((v: any) => selectedCustomParams.includes(v.name))
        .map((v: any) => ({ name: v.name, nodeId: v.nodeId, enable: true })),
      param_value_mapping: paramValueMapping,
      header_value_mapping: headerValueMapping,
      fixed_json_value_mapping: fixedJsonValueMapping,
      // Single-digit: always include the fixed digit. Multi-digit: only if user opted in.
      ...(isSingleDigitDtmf ? { dtmf_digit: dtmfDigit } : {}),
      ...(isMultiDigitDtmf && useDtmfInput
        ? { dtmf_digit: dtmfDigit, use_dtmf_input: true }
        : {}),
    };

    const connectedTargetIds = getEdges()
      .filter((e: any) => e.source === id)
      .map((e: any) => e.target);

    // Single atomic update: save API node action + propagate passToNextNode to connected play/dtmf nodes
    setNodes((nds: any[]) =>
      nds.map((node: any) => {
        if (node.id === id) {
          return { ...node, data: { ...node.data, action: newAction } };
        }
        if (
          connectedTargetIds.includes(node.id) &&
          (node.type === 'playNode' || node.type === 'gatherNode')
        ) {
          return {
            ...node,
            data: { ...node.data, passToNextNode: playInNextNode },
          };
        }
        return node;
      }),
    );

    toast.success('Details updated successfully.');
    setModalIsOpen(false);
  };

  const handleRemoveNode = () => {
    if (data.parent === 'gather Node' && data.idToRemove) {
      setNodes((nodes: any[]) =>
        nodes.map((node: any) => {
          if (!node.data.selectedHandles) return node;
          const updatedHandles = { ...node.data.selectedHandles };
          const updatedSmsData = { ...node.data.smsData };
          delete updatedHandles[data.idToRemove];
          if (updatedSmsData) {
            const parts = data.idToRemove.split('-');
            const handleIndex = parts[parts.length - 1];
            if (handleIndex) delete updatedSmsData[`DTMF_${handleIndex}`];
          }
          return {
            ...node,
            data: {
              ...node.data,
              selectedHandles: updatedHandles,
              smsData: updatedSmsData,
            },
          };
        }),
      );
    }
    onRemove(id);
  };

  // "Replace With" options shared by the payload / header / fixed-JSON drawers.
  const renderReplacementOptions = () => (
    <>
      {replacementFlowVariables.length > 0 && (
        <optgroup label="Flow Variables">
          {replacementFlowVariables.map((v: any) => (
            <option key={`fv-${v.id}`} value={flowVariableToken(v)}>
              {v.name}
            </option>
          ))}
        </optgroup>
      )}
      {ancestorVars.length > 0 && (
        <optgroup label="From Previous Nodes">
          {ancestorVars.map((v: any, j: number) => (
            <option key={j} value={v.name}>
              {v.name}
            </option>
          ))}
        </optgroup>
      )}
    </>
  );

  const configured = !!data.action?.webhook_id;

  // Detect DTMF parent and extract the digit value
  const isDtmfParent = data.parent === 'gather Node';
  const isSingleDigitDtmf = isDtmfParent && !!data.idToRemove;
  const isMultiDigitDtmf = isDtmfParent && !data.idToRemove;
  const dtmfDigit = isSingleDigitDtmf
    ? data.idToRemove.split('_handle-').pop() // known at design time
    : '{dtmf_input}'; // resolved at runtime

  const CheckboxSection = ({
    title,
    entries,
    selectedSet,
    onToggle,
    valueLabel,
  }: any) => (
    <Form.Group className="mb-3">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <Form.Label className="mb-0">{title}</Form.Label>
        <span style={{ fontSize: '12px' }}>
          <button
            type="button"
            onClick={() =>
              entries.forEach(
                ({ key }: any) => !selectedSet.has(key) && onToggle(key),
              )
            }
            style={{
              background: 'none',
              border: 'none',
              color: '#17a2b8',
              cursor: 'pointer',
              padding: '0 6px 0 0',
              fontSize: '12px',
            }}
          >
            Select All
          </button>
          <span>|</span>
          <button
            type="button"
            onClick={() =>
              entries.forEach(
                ({ key }: any) => selectedSet.has(key) && onToggle(key),
              )
            }
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0 0 0 6px',
              fontSize: '12px',
            }}
          >
            Deselect All
          </button>
        </span>
      </div>
      <div
        style={{
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          backgroundColor: '#fff',
          maxHeight: '200px',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          padding: '4px 0',
        }}
      >
        {entries.map(({ key, value }: any) => (
          <label
            key={key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              padding: '5px 12px',
              borderRadius: '4px',
              backgroundColor: selectedSet.has(key) ? '#e8f7f9' : 'transparent',
              margin: 0,
            }}
          >
            <input
              type="checkbox"
              checked={selectedSet.has(key)}
              onChange={() => onToggle(key)}
              style={{
                cursor: 'pointer',
                accentColor: '#17a2b8',
                width: '15px',
                height: '15px',
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: '13px',
                flexShrink: 0,
              }}
            >
              {key}
            </span>
            <span
              style={{
                fontSize: '12px',
                marginLeft: 'auto',
                fontFamily: 'monospace',
              }}
            >
              {valueLabel ? valueLabel(key, value) : value}
            </span>
          </label>
        ))}
      </div>
      <small style={{ marginTop: '4px', display: 'block' }}>
        {selectedSet.size} of {entries.length} selected
      </small>
    </Form.Group>
  );

  return (
    <div className="ivrn" style={{ '--node-color': '#b45309' } as any}>
      <span
        className="ivrn-icon"
        style={{ background: '#b45309', color: '#fff' }}
        onClick={triggerHandleConnect}
      >
        <WebhookIcon size={22} strokeWidth={2} />
      </span>
      <Handle
        type="target"
        position={'left' as any}
        style={{ background: '#b45309', top: '65%' }}
      />
      <Handle
        type="source"
        position={'right' as any}
        style={{ background: '#b45309' }}
      />

      <div className="ivrn-head">
        <span className="ivrn-title">API Webhook</span>
        {!configured && <span className="ivrn-badge ivrn-badge--red">!</span>}
      </div>
      <div className="ivrn-body">
        {configured ? (
          <span className="ivrn-val">
            <CheckCircle2
              size={10}
              style={{ marginRight: 4, color: '#059669', flexShrink: 0 }}
            />
            Webhook configured
          </span>
        ) : (
          <span className="ivrn-hint">Click ⚙ to connect API</span>
        )}
      </div>

      <div className="ivrn-actions">
        <button
          className="ivrn-btn ivrn-btn--cfg"
          title="Configure"
          onClick={openModal}
        >
          <Settings size={14} strokeWidth={2} />
        </button>
        <button
          className="ivrn-btn ivrn-btn--del"
          title="Remove"
          onClick={handleRemoveNode}
        >
          <Trash2 size={14} strokeWidth={2} />
        </button>
      </div>

      <IvrDrawer
        show={modalIsOpen}
        onHide={handleClose}
        title="API Node"
        subtitle="Configure an external API call and map response variables."
        footer={
          <>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </>
        }
      >
        {/* Webhook dropdown */}
        <Form.Group className="mb-3">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '6px',
            }}
          >
            <Form.Label className="mb-0">
              Select API Webhook <span style={{ color: 'red' }}>*</span>
            </Form.Label>
            <a
              href={`/${ROUTES.APIWEBHOOK.CREATE}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: '12px',
                color: '#17a2b8',
                textDecoration: 'none',
              }}
            >
              + Create API Webhook
            </a>
          </div>
          <Form.Control
            as="select"
            value={draftWebhookId}
            onChange={handleWebhookChange}
            isInvalid={!!errors.webhook}
          >
            <option value="">-- Select Webhook --</option>
            {apiWebhookList.map((w: any) => (
              <option key={w.id} value={w.id}>
                {w.webhook_name || w.name}
              </option>
            ))}
          </Form.Control>
          {errors.webhook && (
            <Form.Control.Feedback type="invalid">
              {errors.webhook}
            </Form.Control.Feedback>
          )}
        </Form.Group>

        {/* Payload Parameters — compact row with edit icon */}
        {webhookPayloadInfo &&
          webhookPayloadInfo.entries.length > 0 &&
          (() => {
            const mappedCount = webhookPayloadInfo.entries.filter(
              ([k]: any) => paramValueMapping[k],
            ).length;
            const total = webhookPayloadInfo.entries.length;
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  marginBottom: '12px',
                  background: '#fff',
                }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                >
                  <ClipboardList
                    style={{ width: 15, height: 15, color: '#17a2b8', }}
                  />
                  <span style={{ fontWeight: 600, fontSize: '13px' }}>
                    {webhookPayloadInfo.isPost
                      ? 'Payload Parameters'
                      : 'Request Parameters'}
                  </span>
                  <span
                    style={{
                      fontSize: '11px',
                      background: mappedCount > 0 ? '#e8f7f9' : '#f1f3f5',
                      color: mappedCount > 0 ? '#0c7a8a' : '#6c757d',
                      border: `1px solid ${
                        mappedCount > 0 ? '#b2ebf2' : '#dee2e6'
                      }`,
                      borderRadius: '10px',
                      padding: '1px 8px',
                      fontWeight: 500,
                    }}
                  >
                    {mappedCount > 0
                      ? `${mappedCount} / ${total} replaced`
                      : `${total} params`}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setPayloadEditOpen(true)}
                  title="Edit parameter replacements"
                  style={{
                    background: 'none',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    padding: '4px 10px',
                    cursor: 'pointer',
                    color: '#17a2b8',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    fontSize: '12px',
                  }}
                >
                  <Pencil style={{ width: 12, height: 12, }} />{' '}
                  Edit
                </button>
              </div>
            );
          })()}

        {/* Headers — compact row with edit icon */}
        {webhookHeaderEntries.length > 0 &&
          (() => {
            const hMappedCount = webhookHeaderEntries.filter(
              ([k]: any) => headerValueMapping[k],
            ).length;
            const hTotal = webhookHeaderEntries.length;
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  marginBottom: '12px',
                  background: '#fff',
                }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                >
                  <Key
                    style={{ width: 14, height: 14, color: '#17a2b8', }}
                  />
                  <span style={{ fontWeight: 600, fontSize: '13px' }}>
                    Headers
                  </span>
                  <span
                    style={{
                      fontSize: '11px',
                      background: hMappedCount > 0 ? '#e8f7f9' : '#f1f3f5',
                      color: hMappedCount > 0 ? '#0c7a8a' : '#6c757d',
                      border: `1px solid ${
                        hMappedCount > 0 ? '#b2ebf2' : '#dee2e6'
                      }`,
                      borderRadius: '10px',
                      padding: '1px 8px',
                      fontWeight: 500,
                    }}
                  >
                    {hMappedCount > 0
                      ? `${hMappedCount} / ${hTotal} replaced`
                      : `${hTotal} header${hTotal !== 1 ? 's' : ''}`}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setHeadersEditOpen(true)}
                  title="Edit header replacements"
                  style={{
                    background: 'none',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    padding: '4px 10px',
                    cursor: 'pointer',
                    color: '#17a2b8',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    fontSize: '12px',
                  }}
                >
                  <Pencil style={{ width: 12, height: 12, }} />{' '}
                  Edit
                </button>
              </div>
            );
          })()}

        {/* Single-digit DTMF: auto info banner */}
        {isSingleDigitDtmf && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              background: '#e8f7f9',
              border: '1px solid #b2ebf2',
              borderRadius: '6px',
              padding: '10px 14px',
              marginBottom: '12px',
            }}
          >
            <Info
              style={{ width: 16, height: 16,
                color: '#17a2b8',
                marginTop: '2px',
                flexShrink: 0,
              }}
            />
            <div>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: '13px',
                  color: '#0c7a8a',
                  marginBottom: '3px',
                }}
              >
                DTMF Input Detected
              </div>
              <div style={{ fontSize: '12px', color: '#555' }}>
                Digit value{' '}
                <code
                  style={{
                    background: '#c9f0f5',
                    padding: '1px 5px',
                    borderRadius: '3px',
                    fontWeight: 700,
                    color: '#0c7a8a',
                  }}
                >
                  {dtmfDigit}
                </code>{' '}
                will be automatically passed to the webhook as{' '}
                <code
                  style={{
                    background: '#c9f0f5',
                    padding: '1px 5px',
                    borderRadius: '3px',
                    color: '#0c7a8a',
                  }}
                >
                  dtmf_digit
                </code>
                .
              </div>
            </div>
          </div>
        )}

        {/* Multi-digit DTMF: opt-in checkbox */}
        {isMultiDigitDtmf && dtmfParentPassEnabled && (
          <div
            style={{
              background: '#e8f7f9',
              border: '1px solid #b2ebf2',
              borderRadius: '6px',
              padding: '10px 14px',
              marginBottom: '12px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '6px',
              }}
            >
              <Keyboard
                style={{ width: 15, height: 15,
                  color: '#17a2b8',
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontWeight: 600,
                  fontSize: '13px',
                  color: '#0c7a8a',
                }}
              >
                DTMF Collected Digits Available
              </span>
            </div>
            <Form.Check
              type="checkbox"
              id="use-dtmf-input"
              label={
                <span style={{ fontSize: '12px', color: '#555' }}>
                  Pass collected digits to webhook as{' '}
                  <code
                    style={{
                      background: '#c9f0f5',
                      padding: '1px 5px',
                      borderRadius: '3px',
                      color: '#0c7a8a',
                    }}
                  >
                    dtmf_digit
                  </code>
                </span>
              }
              checked={useDtmfInput}
              onChange={(e: any) => setUseDtmfInput(e.target.checked)}
            />
          </div>
        )}

        {/* Options */}
        <Form.Group className="mb-3">
          <Form.Label className="mb-2" style={{ fontWeight: 600 }}>
            Options
          </Form.Label>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
          >
            <Form.Check
              type="checkbox"
              id="pass-to-webhook"
              label="Pass To Webhook / CDR"
              checked={passToWebhook}
              onChange={(e: any) => setPassToWebhook(e.target.checked)}
            />
            <Form.Check
              type="checkbox"
              id="play-in-next-node"
              label="Make Available for Future Node"
              checked={playInNextNode}
              onChange={(e: any) => {
                setPlayInNextNode(e.target.checked);
                if (!e.target.checked) setSelectedAvailableParams([]);
              }}
            />
          </div>
        </Form.Group>

        {/* Make Available: multiselect of ALL response params */}
        {playInNextNode && (
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600 }}>
              Select Parameters to Make Available
            </Form.Label>
            {responseParams.length === 0 ? (
              <p style={{ color: '#6c757d', fontSize: '13px' }}>
                No response parameters configured for this webhook.
              </p>
            ) : (
              <>
                <Form.Select
                  multiple
                  value={selectedAvailableParams}
                  onChange={(e: any) => {
                    const selected = Array.from(
                      e.target.selectedOptions,
                    ).map((o: any) => o.value);
                    setSelectedAvailableParams(selected);
                  }}
                  style={{ minHeight: 100 }}
                >
                  {responseParams.map((key: any) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                  Hold Ctrl / Cmd to select multiple
                </Form.Text>
              </>
            )}
          </Form.Group>
        )}

        {/* Pass To Webhook: Fixed JSON (edit icon) + Response Parameters */}
        {passToWebhook && (
          <>
            {fixedJsonEntries.length > 0 &&
              (() => {
                const fMappedCount = fixedJsonEntries.filter(
                  ({ key }: any) => fixedJsonValueMapping[key],
                ).length;
                const fTotal = fixedJsonEntries.length;
                return (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px',
                      marginBottom: '12px',
                      background: '#fff',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}
                    >
                      <Code
                        style={{ width: 14, height: 14, color: '#17a2b8', }}
                      />
                      <span style={{ fontWeight: 600, fontSize: '13px' }}>
                        Fixed JSON
                      </span>
                      <span
                        style={{
                          fontSize: '11px',
                          background: fMappedCount > 0 ? '#e8f7f9' : '#f1f3f5',
                          color: fMappedCount > 0 ? '#0c7a8a' : '#6c757d',
                          border: `1px solid ${
                            fMappedCount > 0 ? '#b2ebf2' : '#dee2e6'
                          }`,
                          borderRadius: '10px',
                          padding: '1px 8px',
                          fontWeight: 500,
                        }}
                      >
                        {fMappedCount > 0
                          ? `${fMappedCount} / ${fTotal} replaced`
                          : `${fTotal} params`}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFixedEditOpen(true)}
                      style={{
                        background: 'none',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px',
                        padding: '4px 10px',
                        cursor: 'pointer',
                        color: '#17a2b8',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        fontSize: '12px',
                      }}
                    >
                      <Pencil
                        style={{ width: 12, height: 12, }}
                      />{' '}
                      Edit
                    </button>
                  </div>
                );
              })()}
            {responseParams.length > 0 && (
              <CheckboxSection
                title="Response Parameters"
                entries={responseParams.map((key: any) => ({
                  key,
                  value: `{${key}}`,
                }))}
                selectedSet={selectedResponseParams}
                onToggle={toggleResponseParam}
              />
            )}
            {fixedJsonEntries.length === 0 &&
              responseParams.length === 0 &&
              selectedWebhook && (
                <p style={{ color: '#6c757d', fontSize: '13px', margin: 0 }}>
                  No fixed JSON or response parameters configured for this
                  webhook.
                </p>
              )}
          </>
        )}
      </IvrDrawer>

      {/* Sub-drawer: Payload Parameters */}
      <IvrDrawer
        show={payloadEditOpen}
        onHide={() => setPayloadEditOpen(false)}
        title={
          webhookPayloadInfo?.isPost
            ? 'Payload Parameters'
            : 'Request Parameters'
        }
        subtitle="Map API parameters to dynamic variables from the flow."
        footer={
          <>
            <Button onClick={() => setPayloadEditOpen(false)}>Done</Button>
          </>
        }
      >
        {webhookPayloadInfo && (
          <div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 20px 1fr',
                gap: '8px',
                padding: '8px 16px',
                background: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
              }}
            >
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#6c757d',
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                }}
              >
                Parameter
              </span>
              <span />
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#6c757d',
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                }}
              >
                Replace With
              </span>
            </div>
            <div
              style={{
                maxHeight: '400px',
                overflowY:
                  webhookPayloadInfo.entries.length > 3 ? 'auto' : 'visible',
              }}
            >
              {webhookPayloadInfo.entries.map(
                ([key, value]: any, i: number) => {
                  const replaced = paramValueMapping[key] || '';
                  const defaultVal =
                    typeof value === 'object'
                      ? JSON.stringify(value)
                      : String(value);
                  return (
                    <div
                      key={i}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 20px 1fr',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        borderBottom: '1px solid #f0f0f0',
                        background: replaced ? '#f0fdf4' : '#fff',
                        transition: 'background 0.15s',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '13px',
                            color: '#0c7a8a',
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={key}
                        >
                          {key}
                        </div>
                        <div
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            color: replaced ? '#adb5bd' : '#868e96',
                            textDecoration: replaced
                              ? 'line-through'
                              : 'none',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            marginTop: '2px',
                          }}
                          title={defaultVal}
                        >
                          {defaultVal}
                        </div>
                      </div>
                      <ArrowRight
                        style={{ width: 14, height: 14,
                          color: replaced ? '#17a2b8' : '#dee2e6',
                          textAlign: 'center',
                        }}
                      />
                      <Form.Select
                        value={replaced}
                        onChange={(e) =>
                          setParamValueMapping((prev: any) => {
                            const next = { ...prev };
                            if (e.target.value) next[key] = e.target.value;
                            else delete next[key];
                            return next;
                          })
                        }
                        style={{
                          fontFamily: 'monospace',
                          fontSize: '13px',
                          borderColor: replaced ? '#17a2b8' : '#ced4da',
                          color: replaced ? '#0c7a8a' : '#6c757d',
                          background: replaced ? '#e8f7f9' : '#fff',
                        }}
                      >
                        <option value="">— no replacement —</option>
                        {renderReplacementOptions()}
                      </Form.Select>
                    </div>
                  );
                },
              )}
            </div>
          </div>
        )}
      </IvrDrawer>

      {/* Sub-drawer: Headers */}
      <IvrDrawer
        show={headersEditOpen}
        onHide={() => setHeadersEditOpen(false)}
        title="Headers"
        subtitle="Map request headers to dynamic variables from the flow."
        footer={
          <>
            <Button onClick={() => setHeadersEditOpen(false)}>Done</Button>
          </>
        }
      >
        <div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 20px 1fr',
              gap: '8px',
              padding: '8px 16px',
              background: '#f8f9fa',
              borderBottom: '1px solid #dee2e6',
            }}
          >
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#6c757d',
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
              }}
            >
              Header
            </span>
            <span />
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#6c757d',
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
              }}
            >
              Replace With
            </span>
          </div>
          <div
            style={{
              maxHeight: '400px',
              overflowY:
                webhookHeaderEntries.length > 3 ? 'auto' : 'visible',
            }}
          >
            {webhookHeaderEntries.map(([key, value]: any, i: number) => {
              const replaced = headerValueMapping[key] || '';
              const defaultVal = String(value);
              return (
                <div
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 20px 1fr',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    borderBottom: '1px solid #f0f0f0',
                    background: replaced ? '#f0fdf4' : '#fff',
                    transition: 'background 0.15s',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        color: '#0c7a8a',
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={key}
                    >
                      {key}
                    </div>
                    <div
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        color: replaced ? '#adb5bd' : '#868e96',
                        textDecoration: replaced ? 'line-through' : 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginTop: '2px',
                      }}
                      title={defaultVal}
                    >
                      {defaultVal}
                    </div>
                  </div>
                  <ArrowRight
                    style={{ width: 14, height: 14,
                      color: replaced ? '#17a2b8' : '#dee2e6',
                      textAlign: 'center',
                    }}
                  />
                  <Form.Select
                    value={replaced}
                    onChange={(e) =>
                      setHeaderValueMapping((prev: any) => {
                        const next = { ...prev };
                        if (e.target.value) next[key] = e.target.value;
                        else delete next[key];
                        return next;
                      })
                    }
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      borderColor: replaced ? '#17a2b8' : '#ced4da',
                      color: replaced ? '#0c7a8a' : '#6c757d',
                      background: replaced ? '#e8f7f9' : '#fff',
                    }}
                  >
                    <option value="">— no replacement —</option>
                    {renderReplacementOptions()}
                  </Form.Select>
                </div>
              );
            })}
          </div>
        </div>
      </IvrDrawer>

      {/* Sub-drawer: Fixed JSON */}
      <IvrDrawer
        show={fixedEditOpen}
        onHide={() => setFixedEditOpen(false)}
        title="Fixed JSON Parameters"
        subtitle="Replace fixed JSON values with dynamic variables from the flow."
        footer={
          <>
            <Button onClick={() => setFixedEditOpen(false)}>Done</Button>
          </>
        }
      >
        <div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 20px 1fr',
              gap: '8px',
              padding: '8px 16px',
              background: '#f8f9fa',
              borderBottom: '1px solid #dee2e6',
            }}
          >
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#6c757d',
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
              }}
            >
              Parameter
            </span>
            <span />
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#6c757d',
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
              }}
            >
              Replace With
            </span>
          </div>
          <div
            style={{
              maxHeight: '400px',
              overflowY: fixedJsonEntries.length > 3 ? 'auto' : 'visible',
            }}
          >
            {fixedJsonEntries.map(({ key, value }: any, i: number) => {
              const replaced = fixedJsonValueMapping[key] || '';
              return (
                <div
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 20px 1fr',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    borderBottom: '1px solid #f0f0f0',
                    background: replaced ? '#f0fdf4' : '#fff',
                    transition: 'background 0.15s',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        color: '#0c7a8a',
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={key}
                    >
                      {key}
                    </div>
                    <div
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        color: replaced ? '#adb5bd' : '#868e96',
                        textDecoration: replaced ? 'line-through' : 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginTop: '2px',
                      }}
                      title={value}
                    >
                      {value}
                    </div>
                  </div>
                  <ArrowRight
                    style={{ width: 14, height: 14,
                      color: replaced ? '#17a2b8' : '#dee2e6',
                      textAlign: 'center',
                    }}
                  />
                  <Form.Select
                    value={replaced}
                    onChange={(e) =>
                      setFixedJsonValueMapping((prev: any) => {
                        const next = { ...prev };
                        if (e.target.value) next[key] = e.target.value;
                        else delete next[key];
                        return next;
                      })
                    }
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      borderColor: replaced ? '#17a2b8' : '#ced4da',
                      color: replaced ? '#0c7a8a' : '#6c757d',
                      background: replaced ? '#e8f7f9' : '#fff',
                    }}
                  >
                    <option value="">— no replacement —</option>
                    {renderReplacementOptions()}
                  </Form.Select>
                </div>
              );
            })}
          </div>
        </div>
      </IvrDrawer>
    </div>
  );
};

export default ApiNode;
